import { type StateCreator } from 'zustand'

import { scopedTranslation } from '@utils/I18n'
import type { SelectOption } from '@utils/types'

import { type StoreState as FilteringParamsStoreState } from './useSupporterFilteringStore'
import useSupportersSearch from '../../shared/hooks/useSupportersSearch'
import { type SharedInertiaProps } from '../../../types/groupTypes'

const tAttributes = scopedTranslation('attributes')
const tShared = scopedTranslation('shared')

export type AvailableFilterPredicate = {
  label: string
  value: string
  array: boolean
  defaultValue?: boolean
}

// A predicate with a defaultValue needs nothing from the user (Exists, Never):
// its row starts, and stays, on that fixed value.
export const requiresValue = (predicate?: AvailableFilterPredicate) => predicate?.defaultValue === undefined

export const findPredicate = (predicates: AvailableFilterPredicate[], predicate: string | undefined) =>
  predicates.find((available) => available.value === predicate)

export const initialValues = (predicate?: AvailableFilterPredicate) => predicate?.defaultValue ?? null

export type AvailableFilter = {
  value: string
  label: string
  type: string
  predicates: AvailableFilterPredicate[]
}

export type FilterValue = string[] | string | boolean | null

export type BaseActiveFilter = {
  property: string
  predicate: string
  values: FilterValue
}

export type ActiveFilterWithSelectOption =
  | BaseActiveFilter
  | {
      property: BaseActiveFilter['property']
      predicate: BaseActiveFilter['predicate']
      values: SelectOption[]
    }

export type ActiveFilterWithValueType<T> = {
  property: BaseActiveFilter['property']
  predicate: BaseActiveFilter['predicate']
  values: T
}

export type AvailableFilters = Record<string, AvailableFilter>

export const availableFilters: AvailableFilters = {
  first_name: {
    value: 'first_name',
    label: tAttributes('supporter.first_name'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.i_cont'),
        value: 'i_cont',
        array: false,
      },
      {
        label: tShared('predicates.not_i_cont'),
        value: 'not_i_cont',
        array: false,
      },
      {
        label: tShared('predicates.start'),
        value: 'start',
        array: false,
      },
    ],
  },
  last_name: {
    value: 'last_name',
    label: tAttributes('supporter.last_name'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.i_cont'),
        value: 'i_cont',
        array: false,
      },
      {
        label: tShared('predicates.not_i_cont'),
        value: 'not_i_cont',
        array: false,
      },
      {
        label: tShared('predicates.start'),
        value: 'start',
        array: false,
      },
    ],
  },
  email: {
    value: 'email',
    label: tAttributes('supporter.email'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  primary_mobile: {
    value: 'primary_mobile',
    label: tAttributes('supporter.mobile'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  primary_phone: {
    value: 'primary_phone',
    label: tAttributes('supporter.phone'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  city: {
    value: 'city',
    label: tAttributes('supporter.city'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  state: {
    value: 'state',
    label: tAttributes('supporter.state'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  zip: {
    value: 'zip',
    label: tAttributes('supporter.zip'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.start'),
        value: 'start',
        array: false,
      },
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  note: {
    value: 'note',
    label: tAttributes('supporter.note'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.i_cont'),
        value: 'i_cont',
        array: false,
      },
      {
        label: tShared('predicates.not_i_cont'),
        value: 'not_i_cont',
        array: false,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  supporter_notes: {
    value: 'supporter_notes',
    label: tAttributes('supporter.supporter_notes'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.i_cont'),
        value: 'i_cont',
        array: false,
      },
      {
        label: tShared('predicates.not_i_cont'),
        value: 'not_i_cont',
        array: false,
      },
    ],
  },
  engagement_level: {
    value: 'engagement_level',
    label: tAttributes('supporter.engagement_level'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  emailable: {
    value: 'emailable',
    label: tAttributes('supporter.emailable'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.true'),
        value: 'true',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.false'),
        value: 'false',
        defaultValue: true,
        array: false,
      },
    ],
  },
  textable: {
    value: 'textable',
    label: tAttributes('supporter.textable'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.true'),
        value: 'true',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.false'),
        value: 'false',
        defaultValue: true,
        array: false,
      },
    ],
  },
  callable: {
    value: 'callable',
    label: tAttributes('supporter.callable'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.true'),
        value: 'true',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.false'),
        value: 'false',
        defaultValue: true,
        array: false,
      },
    ],
  },
  tags: {
    value: 'tags',
    label: tShared('resource_names.tags'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.has_any'),
        value: 'has_any',
        array: true,
      },
      {
        label: tShared('predicates.has_all'),
        value: 'has_all',
        array: true,
      },
      {
        label: tShared('predicates.not_any'),
        value: 'not_any',
        array: true,
      },
    ],
  },
  event_rsvps: {
    value: 'event_rsvps',
    label: tAttributes('supporter.event_rsvps'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.has_any'),
        value: 'has_any',
        array: true,
      },
      {
        label: tShared('predicates.has_all'),
        value: 'has_all',
        array: true,
      },
      {
        label: tShared('predicates.not_any'),
        value: 'not_any',
        array: true,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  supranational_district: {
    value: 'supranational_district',
    label: tAttributes('supporter.supranational_district'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
    ],
  },
  federal_district: {
    value: 'federal_district',
    label: tAttributes('supporter.federal_district'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
      {
        label: tShared('predicates.present'),
        value: 'present',
        defaultValue: true,
        array: false,
      },
      {
        label: tShared('predicates.blank'),
        value: 'blank',
        defaultValue: true,
        array: false,
      },
    ],
  },
  county_district: {
    value: 'county_district',
    label: tAttributes('supporter.county_district'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
    ],
  },
  state_lower_district: {
    value: 'state_lower_district',
    label: tAttributes('supporter.state_lower_district'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
    ],
  },
  school_district: {
    value: 'school_district',
    label: tAttributes('supporter.school_district'),
    type: 'string',
    predicates: [
      {
        label: tShared('predicates.in'),
        value: 'in',
        array: true,
      },
      {
        label: tShared('predicates.not_in'),
        value: 'not_in',
        array: true,
      },
    ],
  },
  last_contacted_at: {
    value: 'last_contacted_at',
    label: tAttributes('supporter.last_contacted_at'),
    type: 'date',
    predicates: [
      {
        label: tShared('predicates.lt.iso_8601_date_time'),
        value: 'lt',
        array: false,
      },
      {
        label: tShared('predicates.eq.iso_8601_date_time'),
        value: 'eq',
        array: false,
      },
      {
        label: tShared('predicates.gt.iso_8601_date_time'),
        value: 'gt',
        array: false,
      },
      {
        label: tShared('predicates.null.iso_8601_date_time'),
        value: 'null',
        defaultValue: true,
        array: false,
      },
    ],
  },
  external_created_at: {
    value: 'external_created_at',
    label: tAttributes('supporter.external_created_at'),
    type: 'date',
    predicates: [
      {
        label: tShared('predicates.lt.iso_8601_date_time'),
        value: 'lt',
        array: false,
      },
      {
        label: tShared('predicates.eq.iso_8601_date_time'),
        value: 'eq',
        array: false,
      },
      {
        label: tShared('predicates.gt.iso_8601_date_time'),
        value: 'gt',
        array: false,
      },
    ],
  },
  action_group_members_created_at: {
    value: 'action_group_members_created_at',
    label: tAttributes('supporter.action_group_members_created_at'),
    type: 'date',
    predicates: [
      {
        label: tShared('predicates.lt.iso_8601_date_time'),
        value: 'lt',
        array: false,
      },
      {
        label: tShared('predicates.eq.iso_8601_date_time'),
        value: 'eq',
        array: false,
      },
      {
        label: tShared('predicates.gt.iso_8601_date_time'),
        value: 'gt',
        array: false,
      },
    ],
  },
}

// TODO: Create a predicates hash
// Reference it in the availableFilters hash
// And export this const as all predicates where array: true
const allArrayPredicates = Object.entries(availableFilters).reduce((acc, [_, filter]) => {
  return [...acc, ...filter.predicates.filter((predicate) => predicate.array).map((predicate) => predicate.value)]
}, [])

const arrayPredicates = [...new Set(allArrayPredicates)]

export function usesArrayPredicate(filter: BaseActiveFilter): filter is ActiveFilterWithValueType<string[]>
export function usesArrayPredicate(
  filter: ActiveFilterWithSelectOption
): filter is ActiveFilterWithValueType<SelectOption[]>

export function usesArrayPredicate(filter: BaseActiveFilter) {
  return arrayPredicates.includes(filter.predicate)
}

function getRestrictedFilterKeys(permissions: SharedInertiaProps['permissions']): string[] {
  const restrictedKeys: string[] = []

  if (!permissions.electoralFiltersEnabled) {
    restrictedKeys.push(
      'supranational_district',
      'county_district',
      'state_lower_district',
      'school_district'
    )
  }

  if (!permissions.editFederalDistrict && !permissions.electoralFiltersEnabled) {
    restrictedKeys.push('federal_district')
  }

  return restrictedKeys
}

function availableFiltersByPermissions(permissions: SharedInertiaProps['permissions']) {
  const restrictedKeys = getRestrictedFilterKeys(permissions)

  if (restrictedKeys.length === 0) {
    return availableFilters
  }

  return Object.fromEntries(Object.entries(availableFilters).filter(([key]) => !restrictedKeys.includes(key)))
}

export type StoreState = {
  filters: BaseActiveFilter[]
  availableFilters: AvailableFilters
}

export type StoreActions = {
  removeFilters: (filtersToRemove: BaseActiveFilter[]) => void
  addFilters: (filtersToAdd: BaseActiveFilter[]) => void
  replaceFilters: (newFilters: BaseActiveFilter[]) => void
}

type SliceCreator = StateCreator<FilteringParamsStoreState, [], [], StoreState & StoreActions>
type StateCreatorWithPermissions = (
  ...args: [...[permissions: SharedInertiaProps['permissions']], ...Parameters<SliceCreator>]
) => ReturnType<SliceCreator>

export const createFilterSlice: StateCreatorWithPermissions = (permissions, set, get, _store) => {
  return {
    availableFilters: availableFiltersByPermissions(permissions),
    filters: [],
    removeFilters(filtersToRemove: BaseActiveFilter[]) {
      const newFilters = get().filters.filter((filter) => !filtersToRemove.includes(filter))
      useSupportersSearch({
        activeFilters: newFilters,
        currentSort: get().currentSort,
        supporterName: get().supporterName,
        setLoading: get().setLoading,
      })
    },
    addFilters(filtersToAdd: BaseActiveFilter[]) {
      const newFilters = [...get().filters, ...filtersToAdd]
      useSupportersSearch({
        activeFilters: newFilters,
        currentSort: get().currentSort,
        supporterName: get().supporterName,
        setLoading: get().setLoading,
      })
    },
    replaceFilters(newFilters: BaseActiveFilter[]) {
      useSupportersSearch({
        activeFilters: newFilters,
        currentSort: get().currentSort,
        supporterName: get().supporterName,
        setLoading: get().setLoading,
      })
    },
  }
}
